/**
 * Bundle Builder
 * Handles "Add to Bundle" functionality on collection pages.
 * Hijacks existing "Add to Cart" buttons on single-variant product cards,
 * manages a sticky bottom bar with tiered discount preview,
 * persists bundle state via localStorage, and adds all items to cart.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'skin-control-bundle';

  /** @type {HTMLElement | null} */
  var bar = null;

  /** @type {Array<{variantId: string, handle: string, title: string, price: number, image: string}>} */
  var bundleItems = [];

  var config = {
    tiers: [{ min: 4, discount: 10 }],
    moneyFormat: '${{amount}}',
    ctaText: 'Add Bundle to Cart',
    minItemsText: 'Add at least [min] items',
    progressText: 'Add [remaining] more for [discount]% off!',
    discountAppliedText: '[discount]% discount applied!',
    savingsText: 'You save [savings]',
  };

  var els = {
    progress: null,
    count: null,
    thumbnails: null,
    originalPrice: null,
    discountedPrice: null,
    savings: null,
    cta: null,
    ctaText: null,
    error: null,
  };

  // =========================================================
  // Initialisation
  // =========================================================

  function init() {
    bar = document.querySelector('[data-bundle-bar]');
    if (!bar) return;

    // Parse tier config
    var tiers = [{ min: 4, discount: 10 }];
    try {
      var parsed = JSON.parse(bar.dataset.tiers || '[]');
      if (Array.isArray(parsed) && parsed.length > 0) {
        tiers = parsed
          .filter(function (t) { return t.min > 0 && t.discount > 0; })
          .sort(function (a, b) { return a.min - b.min; });
      }
    } catch (e) {
      // Keep default
    }

    config = {
      tiers: tiers,
      moneyFormat: bar.dataset.moneyFormat || (window.theme && window.theme.moneyFormat) || '${{amount}}',
      ctaText: bar.dataset.ctaText || 'Add Bundle to Cart',
      minItemsText: bar.dataset.minItemsText || 'Add at least [min] items',
      progressText: bar.dataset.progressText || 'Add [remaining] more for [discount]% off!',
      discountAppliedText: bar.dataset.discountAppliedText || '[discount]% discount applied!',
      savingsText: bar.dataset.savingsText || 'You save [savings]',
    };

    // Cache bar element refs
    els = {
      progress: bar.querySelector('[data-bundle-progress]'),
      count: bar.querySelector('[data-bundle-count]'),
      thumbnails: bar.querySelector('[data-bundle-thumbnails]'),
      originalPrice: bar.querySelector('[data-bundle-original]'),
      discountedPrice: bar.querySelector('[data-bundle-discounted]'),
      savings: bar.querySelector('[data-bundle-savings]'),
      cta: bar.querySelector('[data-bundle-cta]'),
      ctaText: bar.querySelector('[data-bundle-cta-text]'),
      error: bar.querySelector('[data-bundle-error]'),
    };

    restoreBundle();
    hijackButtons();
    document.body.classList.add('bundle-bar-active');

    // Delegated click handler for bar interactions (thumbnails, CTA)
    document.addEventListener('click', handleBarClick);

    // Watch for new cards loaded by Ajaxinate / infinite scroll
    observeNewCards();

    updateBar();
  }

  // =========================================================
  // LocalStorage persistence
  // =========================================================

  function restoreBundle() {
    try {
      var saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        var parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          bundleItems = parsed.filter(function (item) {
            return item.variantId && item.handle && typeof item.price === 'number';
          });
        }
      }
    } catch (e) {
      bundleItems = [];
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  function saveBundle() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(bundleItems));
    } catch (e) {
      // Storage full or unavailable
    }
  }

  // =========================================================
  // Button hijacking
  // =========================================================

  /**
   * Finds all single-variant product cards and converts their
   * "Add to Cart" button into an "Add to Bundle" toggle.
   */
  function hijackButtons() {
    var cards = document.querySelectorAll('[data-product-block]:not(.soldout)');

    cards.forEach(function (card) {
      var form = card.querySelector('[data-product-form]');
      if (!form) return; // Multi-variant products have no form on the grid

      var addBtn = form.querySelector('[data-add-to-cart]');
      if (!addBtn) return;

      // Skip already-processed buttons
      if (addBtn.hasAttribute('data-bundle-hijacked')) return;

      var textSpan = form.querySelector('[data-add-to-cart-text]');
      if (!textSpan) return;

      // Extract product data from the card
      var titleEl = card.querySelector('.product-grid-item__title');
      var handle = extractHandle(titleEl ? titleEl.getAttribute('href') || '' : '');
      var variantInput = form.querySelector('input[name="id"]');
      var variantId = variantInput ? variantInput.value : '';
      var title = titleEl ? titleEl.textContent.trim() : '';
      var price = getPriceFromButton(textSpan);
      var image = getImageFromCard(card);

      if (!handle || !variantId) return;

      // Prevent normal form submission by changing button type
      addBtn.type = 'button';
      addBtn.removeAttribute('name');

      // Store product data on the button
      addBtn.dataset.bundleHijacked = '';
      addBtn.dataset.handle = handle;
      addBtn.dataset.variantId = variantId;
      addBtn.dataset.price = String(price);
      addBtn.dataset.title = title;
      addBtn.dataset.image = image;

      // Check if already in bundle (from localStorage restore)
      var isInBundle = bundleItems.some(function (item) {
        return item.handle === handle;
      });

      // Sync stored bundle item data if card has newer info
      if (isInBundle) {
        var existing = bundleItems.find(function (item) { return item.handle === handle; });
        if (existing) {
          existing.variantId = variantId;
          existing.price = price;
          existing.image = image;
        }
      }

      // Set initial button text
      if (isInBundle) {
        setButtonToRemove(textSpan);
        addBtn.classList.add('bundle-btn--added');
      } else {
        setButtonToAdd(textSpan, price);
      }

      // Intercept clicks (capture phase to fire before theme handlers)
      addBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        toggleItem(addBtn);
      }, true);

      // Safety net: prevent form submission entirely
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        e.stopPropagation();
      }, true);
    });
  }

  function setButtonToAdd(textSpan, price) {
    textSpan.innerHTML = 'Add to Bundle &nbsp;&nbsp; ' + formatMoney(price);
  }

  function setButtonToRemove(textSpan) {
    textSpan.textContent = 'Remove from Bundle';
  }

  // =========================================================
  // Data extraction helpers
  // =========================================================

  /**
   * Extracts product handle from a URL path.
   * @param {string} href
   * @returns {string}
   */
  function extractHandle(href) {
    if (!href) return '';
    var match = href.match(/\/products\/([^/?#]+)/);
    return match && match[1] ? match[1] : '';
  }

  /**
   * Extracts the price in cents from the "Add to Cart" button text.
   * Clones the span, removes <s> (compare-at-price), then parses.
   * @param {HTMLElement} textSpan
   * @returns {number}
   */
  function getPriceFromButton(textSpan) {
    var clone = textSpan.cloneNode(true);
    var strikeThroughs = clone.querySelectorAll('s, .product-price__compare');
    strikeThroughs.forEach(function (el) { el.remove(); });

    var text = clone.textContent || '';
    var cleaned = text.replace(/[^0-9.,]/g, '');
    if (!cleaned) return 0;

    var lastComma = cleaned.lastIndexOf(',');
    var lastPeriod = cleaned.lastIndexOf('.');
    var amount;

    if (lastComma > lastPeriod) {
      amount = parseFloat(cleaned.replace(/\./g, '').replace(',', '.'));
    } else {
      amount = parseFloat(cleaned.replace(/,/g, ''));
    }

    if (isNaN(amount)) return 0;
    return Math.round(amount * 100);
  }

  /**
   * Gets the featured product image src from a card.
   * @param {HTMLElement} card
   * @returns {string}
   */
  function getImageFromCard(card) {
    var imageContainer = card.querySelector('[data-product-image]');
    if (!imageContainer) return '';

    var img = imageContainer.tagName === 'IMG'
      ? imageContainer
      : imageContainer.querySelector('img');
    return img ? (img.currentSrc || img.src || '') : '';
  }

  // =========================================================
  // Toggle item in/out of bundle
  // =========================================================

  function toggleItem(btn) {
    var handle = btn.dataset.handle;
    var card = btn.closest('[data-product-block]');
    var textSpan = card ? card.querySelector('[data-add-to-cart-text]') : null;
    if (!handle || !textSpan) return;

    var existingIndex = bundleItems.findIndex(function (item) {
      return item.handle === handle;
    });

    if (existingIndex > -1) {
      // Remove from bundle
      bundleItems.splice(existingIndex, 1);
      btn.classList.remove('bundle-btn--added');
      setButtonToAdd(textSpan, parseInt(btn.dataset.price || '0', 10));
    } else {
      // Add to bundle
      bundleItems.push({
        variantId: btn.dataset.variantId || '',
        handle: handle,
        title: btn.dataset.title || '',
        price: parseInt(btn.dataset.price || '0', 10),
        image: btn.dataset.image || '',
      });
      btn.classList.add('bundle-btn--added');
      setButtonToRemove(textSpan);
    }

    saveBundle();
    updateBar();
  }

  // =========================================================
  // Click handling (bar interactions)
  // =========================================================

  function handleBarClick(e) {
    var target = e.target;
    if (!target) return;

    // Thumbnail remove click
    var thumb = target.closest('[data-bundle-thumb]');
    if (thumb) {
      e.preventDefault();
      var handle = thumb.dataset.bundleThumb;
      if (handle) removeItemByHandle(handle);
      return;
    }

    // CTA click
    var cta = target.closest('[data-bundle-cta]');
    if (cta && !cta.disabled) {
      console.log('[Bundle] CTA clicked');
      e.preventDefault();
      addBundleToCart();
      return;
    }
  }

  /**
   * Removes an item by handle and syncs the corresponding card button.
   * @param {string} handle
   */
  function removeItemByHandle(handle) {
    var index = bundleItems.findIndex(function (item) {
      return item.handle === handle;
    });
    if (index === -1) return;

    bundleItems.splice(index, 1);

    // Revert the card button to "Add to Bundle"
    var btns = document.querySelectorAll('[data-bundle-hijacked]');
    btns.forEach(function (btn) {
      if (btn.dataset.handle === handle) {
        btn.classList.remove('bundle-btn--added');
        var card = btn.closest('[data-product-block]');
        var textSpan = card ? card.querySelector('[data-add-to-cart-text]') : null;
        if (textSpan) {
          setButtonToAdd(textSpan, parseInt(btn.dataset.price || '0', 10));
        }
      }
    });

    saveBundle();
    updateBar();
  }

  // =========================================================
  // Pricing & tier logic
  // =========================================================

  /**
   * Returns the highest tier the customer currently qualifies for, or null.
   * @param {number} itemCount
   * @returns {{min: number, discount: number} | null}
   */
  function getCurrentTier(itemCount) {
    var current = null;
    for (var i = 0; i < config.tiers.length; i++) {
      if (itemCount >= config.tiers[i].min) {
        current = config.tiers[i];
      }
    }
    return current;
  }

  /**
   * Returns the next tier above the item count, or null if at max.
   * @param {number} itemCount
   * @returns {{min: number, discount: number} | null}
   */
  function getNextTier(itemCount) {
    for (var i = 0; i < config.tiers.length; i++) {
      if (itemCount < config.tiers[i].min) {
        return config.tiers[i];
      }
    }
    return null;
  }

  function calculatePricing() {
    var itemCount = bundleItems.length;
    var originalTotal = bundleItems.reduce(function (sum, item) {
      return sum + item.price;
    }, 0);
    var currentTier = getCurrentTier(itemCount);
    var nextTier = getNextTier(itemCount);
    var hasDiscount = currentTier !== null;
    var discountPercent = hasDiscount ? currentTier.discount : 0;
    var discountAmount = hasDiscount
      ? Math.round(originalTotal * (discountPercent / 100))
      : 0;
    var discountedTotal = originalTotal - discountAmount;

    return {
      originalTotal: originalTotal,
      discountedTotal: discountedTotal,
      discountAmount: discountAmount,
      hasDiscount: hasDiscount,
      currentTier: currentTier,
      nextTier: nextTier,
    };
  }

  // =========================================================
  // Money formatting
  // =========================================================

  /**
   * Formats cents into a money string using the store's money format.
   * Handles {{amount}}, {{amount_no_decimals}}, {{amount_with_comma_separator}},
   * and {{amount_no_decimals_with_comma_separator}}.
   * @param {number} cents
   * @returns {string}
   */
  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    var amountNoDecimals = Math.floor(cents / 100).toString();
    var amountWithComma = amount.replace('.', ',');

    return config.moneyFormat
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount}}', amount);
  }

  // =========================================================
  // Bar UI update
  // =========================================================

  function updateBar() {
    if (!bar) return;

    var itemCount = bundleItems.length;
    var pricing = calculatePricing();
    var firstTier = config.tiers[0];

    // Progress text
    if (els.progress) {
      if (pricing.nextTier) {
        var remaining = pricing.nextTier.min - itemCount;
        els.progress.textContent = config.progressText
          .replace('[remaining]', String(remaining))
          .replace('[discount]', String(pricing.nextTier.discount));
      } else if (pricing.currentTier) {
        els.progress.textContent = config.discountAppliedText
          .replace('[discount]', String(pricing.currentTier.discount));
      } else {
        els.progress.textContent = config.progressText
          .replace('[remaining]', String(firstTier.min))
          .replace('[discount]', String(firstTier.discount));
      }
    }

    // Item count
    if (els.count) {
      els.count.textContent = itemCount + (itemCount === 1 ? ' item' : ' items');
    }

    // Thumbnails
    if (els.thumbnails) {
      els.thumbnails.innerHTML = '';
      bundleItems.forEach(function (item) {
        var thumb = document.createElement('div');
        thumb.className = 'bundle-bar__thumb';
        thumb.dataset.bundleThumb = item.handle;
        thumb.title = item.title + ' \u2014 click to remove';
        thumb.innerHTML =
          '<img src="' + escapeAttr(item.image) + '" alt="' + escapeAttr(item.title) + '" width="48" height="48" loading="lazy">' +
          '<span class="bundle-bar__thumb-remove" aria-label="Remove">&times;</span>';
        els.thumbnails.appendChild(thumb);
      });
    }

    // Pricing display
    if (itemCount > 0 && pricing.hasDiscount) {
      if (els.originalPrice) {
        els.originalPrice.textContent = formatMoney(pricing.originalTotal);
        els.originalPrice.hidden = false;
      }
      if (els.discountedPrice) {
        els.discountedPrice.textContent = formatMoney(pricing.discountedTotal);
      }
      if (els.savings) {
        els.savings.textContent = config.savingsText.replace('[savings]', formatMoney(pricing.discountAmount));
        els.savings.hidden = false;
      }
    } else if (itemCount > 0) {
      if (els.originalPrice) els.originalPrice.hidden = true;
      if (els.discountedPrice) {
        els.discountedPrice.textContent = formatMoney(pricing.originalTotal);
      }
      if (els.savings) els.savings.hidden = true;
    } else {
      if (els.originalPrice) els.originalPrice.hidden = true;
      if (els.discountedPrice) els.discountedPrice.textContent = '';
      if (els.savings) els.savings.hidden = true;
    }

    // CTA button state
    if (els.cta) {
      if (pricing.hasDiscount) {
        els.cta.disabled = false;
        if (els.ctaText) els.ctaText.textContent = config.ctaText;
      } else {
        els.cta.disabled = true;
        if (els.ctaText) {
          els.ctaText.textContent = config.minItemsText.replace('[min]', String(firstTier.min));
        }
      }
    }
  }

  // =========================================================
  // Cart integration
  // =========================================================

  function addBundleToCart() {
    if (!getCurrentTier(bundleItems.length)) return;

    console.log('[Bundle] addBundleToCart called, items:', bundleItems.length);

    if (els.cta) els.cta.disabled = true;
    if (els.error) els.error.hidden = true;

    var items = bundleItems.map(function (item) {
      console.log('[Bundle]   item:', item.title, 'variant:', item.variantId);
      return { id: parseInt(item.variantId, 10), quantity: 1 };
    });

    var cartAddUrl = (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add.js';
    console.log('[Bundle] POST', cartAddUrl);

    fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ items: items }),
    })
      .then(function (response) {
        console.log('[Bundle] Response status:', response.status, response.statusText);
        if (!response.ok) {
          throw new Error('Cart request failed (' + response.status + '). Please wait a moment and try again.');
        }
        return response.json();
      })
      .then(function (data) {
        console.log('[Bundle] Cart add response:', data);
        if (data.status) {
          throw new Error(data.message || 'Failed to add items to cart');
        }

        console.log('[Bundle] Success — clearing bundle and opening cart drawer');

        // Clear bundle
        bundleItems = [];
        localStorage.removeItem(STORAGE_KEY);

        // Reset all hijacked buttons
        document.querySelectorAll('[data-bundle-hijacked]').forEach(function (btn) {
          btn.classList.remove('bundle-btn--added');
          var card = btn.closest('[data-product-block]');
          var textSpan = card ? card.querySelector('[data-add-to-cart-text]') : null;
          if (textSpan) {
            setButtonToAdd(textSpan, parseInt(btn.dataset.price || '0', 10));
          }
        });

        updateBar();

        // Update cart UI and open drawer
        openCartAfterAdd();
      })
      .catch(function (error) {
        console.error('[Bundle] Error:', error.message);
        if (els.error) {
          els.error.textContent = error.message || 'Something went wrong. Please try again.';
          els.error.hidden = false;
          setTimeout(function () {
            if (els.error) els.error.hidden = true;
          }, 5000);
        }
      })
      .then(function () {
        // Re-enable CTA based on current state
        if (els.cta) {
          els.cta.disabled = !getCurrentTier(bundleItems.length);
        }
      });
  }

  // =========================================================
  // Cart UI update after adding bundle
  // =========================================================

  /**
   * Updates the header cart icon and opens the cart drawer after
   * items have been successfully added to the cart.
   *
   * Fetches /cart.js to get updated item count, then triggers
   * the theme's cart drawer toggle which handles loading fresh
   * cart section HTML and opening the drawer.
   */
  function openCartAfterAdd() {
    var cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart_url) || '/cart';
    var cartDrawer = document.querySelector('[data-cart-drawer]');

    if (cartDrawer) {
      console.log('[Bundle] Dispatching theme:cart:refresh to open cart drawer');
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
    } else {
      console.log('[Bundle] No cart drawer found, redirecting to', cartUrl);
      window.location.href = cartUrl;
    }
  }

  // =========================================================
  // MutationObserver for Ajaxinate / infinite scroll
  // =========================================================

  function observeNewCards() {
    var debounceTimer = null;

    var observer = new MutationObserver(function () {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        hijackButtons();
      }, 200);
    });

    // Target the Ajaxinate container specifically, fall back to main content
    var grids = document.querySelectorAll('[id^="AjaxinateLoop"]');
    if (grids.length === 0) {
      grids = document.querySelectorAll('[data-main]');
    }
    if (grids.length === 0) return;

    grids.forEach(function (grid) {
      observer.observe(grid, {
        childList: true,
        subtree: true,
      });
    });
  }

  // =========================================================
  // Utilities
  // =========================================================

  function escapeAttr(str) {
    return (str || '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // Init when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
